import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connector } from '../../../services/api';
import type { WorkflowInterrupt, WorkflowPlan, WorkflowRollbackResult, WorkflowSnapshot } from '../../../types/workflow';
import { isWorkflowApplyResult } from '../../../types/workflow';
import {
  anyGraphToEditable,
  blankState,
  describeWorkflowError,
  downloadJson,
  editableToDsl,
  formatTimestamp,
  graphToEditable,
  nextEdgeId,
  nextStateId,
  removeState,
  renameState,
  shortDigest,
  validateEditable,
  type EditableEdge,
  type EditableWorkflow,
  type WorkflowErrorInfo,
} from '../../../utils/workflowDsl';
import Notification from '../../Notification';
import ConfirmModal from '../../ConfirmModal';
import { useNotification } from '../../../hooks/useNotification';
import WorkflowCanvas, { type WorkflowSelection } from './WorkflowCanvas';
import WorkflowEdgeInspector from './WorkflowEdgeInspector';
import WorkflowGraphInspector from './WorkflowGraphInspector';
import { WorkflowComponentsPage, WorkflowOntologyPage } from './WorkflowLibraryPages';
import WorkflowVersionsPanel from './WorkflowVersionsPanel';
import WorkflowDataPanel from './WorkflowJsonPanel';
import WorkflowPlanModal from './WorkflowPlanModal';
import WorkflowRollbackModal from './WorkflowRollbackModal';
import WorkflowStateConfiguratorView from './WorkflowStateConfiguratorView';
import WorkflowTuningPage from './WorkflowTuningPage';
import { Banner, Pill } from './WorkflowFormControls';
import type { BridgeStates, WorkflowEditBridge } from '../../../types/workflowBridge';
import {
  approxJsonBytes,
  buildProjectionFromEditable,
  collectProjectionWarnings,
  extractWorkflowGlobals,
  stripProjectedAdjacency,
  trimGlobalsToWritableUse,
} from '../../../utils/nltWorkflowProjection';
import './ConnectorWorkflow.css';

export interface ConnectorWorkflowPanelProps {
  integrationId: string;
  /** Saving and rollback are admin-only on the connector; reading is open to integration members. */
  isAdmin: boolean;
  /** Dataset labels for the Tuning page's launch form. */
  datasets?: string[];
  datasetsLoading?: boolean;
  /**
   * Publishes how to read this graph and hand edits back, so the NLT sidebar can tune the
   * workflow. `null` while nothing is loaded. Applying still goes through this panel's own
   * Save… flow — the NLT backend proposes and never writes.
   */
  onNltBridgeChange?: (bridge: WorkflowEditBridge | null) => void;
  /** Открыть окно дообучения. Окно живёт на уровне страницы Коннектора, поэтому
   *  вызывать его можно из любой вкладки — своей проверки здесь не нужно. */
  onOpenFineTuning?: () => void;
}

type SideTab = 'inspector' | 'versions' | 'data';

/** Top-level sub-pages of the Workflow tab. The graph keeps the canvas + side panel; the rest are full card pages. */
type WorkflowPage = 'graph' | 'library' | 'triggers' | 'strategies' | 'ontology' | 'tuning';

const PAGE_TABS: Array<{ id: WorkflowPage; label: string; icon: string }> = [
  { id: 'graph', label: 'Graph', icon: 'fa-project-diagram' },
  { id: 'library', label: 'Library', icon: 'fa-book' },
  { id: 'triggers', label: 'Triggers', icon: 'fa-bolt' },
  { id: 'strategies', label: 'Strategies', icon: 'fa-chess-knight' },
  { id: 'ontology', label: 'Ontology', icon: 'fa-shapes' },
  { id: 'tuning', label: 'Tuning', icon: 'fa-wand-magic-sparkles' },
];

interface PlanState {
  open: boolean;
  loading: boolean;
  applying: boolean;
  data: WorkflowPlan | null;
  error: WorkflowErrorInfo | null;
  comment: string;
}

const CLOSED_PLAN: PlanState = { open: false, loading: false, applying: false, data: null, error: null, comment: '' };

const ConnectorWorkflowPanel: React.FC<ConnectorWorkflowPanelProps> = ({
  integrationId,
  isAdmin,
  datasets = [],
  datasetsLoading = false,
  onNltBridgeChange,
  onOpenFineTuning,
}) => {
  const [snapshot, setSnapshot] = useState<WorkflowSnapshot | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<WorkflowErrorInfo | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const [wf, setWf] = useState<EditableWorkflow | null>(null);
  const [baseDigest, setBaseDigest] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const [selection, setSelection] = useState<WorkflowSelection | null>(null);
  const [sideTab, setSideTab] = useState<SideTab>('inspector');
  const [validationProblems, setValidationProblems] = useState<string[]>([]);

  /** A newer server revision that arrived while local edits were pending. */
  const [pending, setPending] = useState<{ snapshot: WorkflowSnapshot; etag: string | null } | null>(null);
  const [conflict, setConflict] = useState<WorkflowErrorInfo | null>(null);
  const [plan, setPlan] = useState<PlanState>(CLOSED_PLAN);
  const [rollback, setRollback] = useState<{ open: boolean; target: string | null }>({ open: false, target: null });
  const [page, setPage] = useState<WorkflowPage>('graph');
  /** Set → the canvas and side panel are swapped out for that state's full-screen configurator, same as clicking a state on the target system's own admin. */
  const [fullStateViewId, setFullStateViewId] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const importInputRef = useRef<HTMLInputElement | null>(null);
  const { notification, showNotification, hideNotification } = useNotification();
  const notifyRef = useRef(showNotification);
  notifyRef.current = showNotification;

  // Latest values for async callbacks without re-creating them on every render.
  const etagRef = useRef<string | null>(null);
  etagRef.current = etag;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const snapshotRef = useRef<WorkflowSnapshot | null>(null);
  snapshotRef.current = snapshot;

  const applySnapshot = useCallback((snap: WorkflowSnapshot, nextEtag: string | null) => {
    setSnapshot(snap);
    setEtag(nextEtag);
    setBaseDigest(snap.revision?.digest ?? null);
    setWf(snap.workflow?.graph ? graphToEditable(snap.workflow.graph) : null);
    setDirty(false);
    setPending(null);
    setConflict(null);
    setValidationProblems([]);
    setLayoutKey((k) => k + 1);
    setSelection((sel) => (sel && sel.kind === 'state' && snap.workflow?.graph?.states?.[sel.id] ? sel : null));
  }, []);

  const load = useCallback(
    async (opts: { conditional: boolean; silent?: boolean }) => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await connector.getWorkflow(integrationId, { etag: opts.conditional ? etagRef.current : null });
        setLastCheckedAt(new Date());
        if (res.status === 304) {
          if (!opts.silent) notifyRef.current('info', 'Workflow is up to date (304 Not Modified, 0 bytes).');
          return;
        }
        const current = snapshotRef.current;
        if (opts.conditional && current && res.snapshot.revision?.digest && res.snapshot.revision.digest === current.revision?.digest) {
          setEtag(res.etag);
          if (!opts.silent) notifyRef.current('info', 'Workflow is up to date.');
          return;
        }
        if (dirtyRef.current && current) {
          // Never overwrite local edits silently — the optimistic lock exists for exactly this.
          setPending({ snapshot: res.snapshot, etag: res.etag });
          return;
        }
        applySnapshot(res.snapshot, res.etag);
      } catch (err) {
        setLoadError(describeWorkflowError(err, 'Failed to load the workflow.'));
      } finally {
        setLoading(false);
      }
    },
    [integrationId, applySnapshot]
  );

  useEffect(() => {
    setSnapshot(null);
    setEtag(null);
    setWf(null);
    setBaseDigest(null);
    setDirty(false);
    setSelection(null);
    setFullStateViewId(null);
    setPage('graph');
    setPending(null);
    setConflict(null);
    setLoadError(null);
    setValidationProblems([]);
    setPlan(CLOSED_PLAN);
    setIncludeArchived(false);
    etagRef.current = null;
    snapshotRef.current = null;
    dirtyRef.current = false;
    void load({ conditional: false, silent: true });
  }, [integrationId, load]);

  // ─── Local mutations ──────────────────────────────────────────────────────

  const updateWf = useCallback((fn: (prev: EditableWorkflow) => EditableWorkflow) => {
    setWf((prev) => (prev ? fn(prev) : prev));
    setDirty(true);
    setValidationProblems([]);
  }, []);

  const patchState = useCallback(
    (id: string, patch: Record<string, unknown>) =>
      updateWf((prev) => {
        const s: Record<string, unknown> = { ...prev.states[id] };
        Object.keys(patch).forEach((k) => {
          if (patch[k] === undefined) delete s[k];
          else s[k] = patch[k];
        });
        return { ...prev, states: { ...prev.states, [id]: s as EditableWorkflow['states'][string] } };
      }),
    [updateWf]
  );

  const handleRenameState = useCallback(
    (oldId: string, newId: string) => {
      updateWf((prev) => renameState(prev, oldId, newId));
      setSelection({ kind: 'state', id: newId });
      setFullStateViewId((current) => (current === oldId ? newId : current));
    },
    [updateWf]
  );

  const handleDeleteState = useCallback(
    (id: string) => {
      updateWf((prev) => removeState(prev, id));
      setSelection(null);
      setFullStateViewId((current) => (current === id ? null : current));
    },
    [updateWf]
  );

  const handleAddState = useCallback(() => {
    if (!wf) return;
    const created = nextStateId(wf.states);
    const state = blankState(created);
    updateWf((prev) => ({ ...prev, entry: prev.entry || created, states: { ...prev.states, [created]: state } }));
    setSelection({ kind: 'state', id: created });
    setSideTab('inspector');
    setPage('graph');
    setFullStateViewId(created);
  }, [wf, updateWf]);

  const handleConnect = useCallback(
    (source: string, target: string) => {
      const current = wf;
      if (!current) return;
      const existing = current.edges.find((e) => e.source === source && e.target === target);
      if (existing) {
        setSelection({ kind: 'edge', id: existing.id });
        setSideTab('inspector');
        notifyRef.current('info', `Transition ${source} → ${target} already exists — opened its settings.`);
        return;
      }
      const edge: EditableEdge = {
        id: nextEdgeId(source, target),
        source,
        target,
        type: 'conditional',
        priority: 0,
        guard: { on_exit_trigger: { name: `${source}_to_${target}`, min_confidence: 0.7 } },
      };
      updateWf((prev) => ({ ...prev, edges: [...prev.edges, edge] }));
      setSelection({ kind: 'edge', id: edge.id });
      setSideTab('inspector');
    },
    [wf, updateWf]
  );

  const patchEdge = useCallback(
    (id: string, patch: Partial<EditableEdge>) =>
      updateWf((prev) => ({
        ...prev,
        edges: prev.edges.map((e) => {
          if (e.id !== id) return e;
          const next: Record<string, unknown> = { ...e };
          Object.keys(patch).forEach((k) => {
            const v = (patch as Record<string, unknown>)[k];
            if (v === undefined) delete next[k];
            else next[k] = v;
          });
          return next as unknown as EditableEdge;
        }),
      })),
    [updateWf]
  );

  const handleDeleteEdge = useCallback(
    (id: string) => {
      updateWf((prev) => ({ ...prev, edges: prev.edges.filter((e) => e.id !== id) }));
      setSelection(null);
    },
    [updateWf]
  );

  const patchGraph = useCallback(
    (patch: Partial<Pick<EditableWorkflow, 'name' | 'entry' | 'metadata'>>) => updateWf((prev) => ({ ...prev, ...patch })),
    [updateWf]
  );

  const setInterrupts = useCallback((next: WorkflowInterrupt[]) => updateWf((prev) => ({ ...prev, interrupts: next })), [updateWf]);

  const selectState = useCallback((id: string) => {
    setSelection({ kind: 'state', id });
    setSideTab('inspector');
    setPage('graph');
    setFullStateViewId(id);
  }, []);

  const selectEdge = useCallback((id: string) => {
    setSelection({ kind: 'edge', id });
    setSideTab('inspector');
    setPage('graph');
    setFullStateViewId(null);
  }, []);

  /** Canvas click: a state opens its full configurator, an edge or empty space opens/uses the side panel. */
  const handleCanvasSelect = useCallback((sel: WorkflowSelection | null) => {
    setSelection(sel);
    setFullStateViewId(sel?.kind === 'state' ? sel.id : null);
  }, []);

  // ─── NLT bridge ───────────────────────────────────────────────────────────

  // The projection is read through refs at send time rather than rebuilt on every
  // keystroke: it is ~450 KB on the live graph, and only its shape matters for the
  // bridge's identity.
  const wfRef = useRef<EditableWorkflow | null>(null);
  wfRef.current = wf;

  const applyProposedStates = useCallback(
    (states: BridgeStates) => {
      const next = stripProjectedAdjacency(states);
      // The proposal is a copy of everything we sent with edits applied, so the state
      // dictionary is replaced wholesale; edges, interrupts and entry are untouched
      // because `adjacency` was ours to add and is not written back.
      updateWf((prev) => ({ ...prev, states: next }));
      setPage('graph');
      setFullStateViewId(null);
      setSelection(null);
    },
    [updateWf]
  );

  const nltWarnings = useMemo(() => {
    const projection = buildProjectionFromEditable(wf, snapshot);
    return projection ? collectProjectionWarnings(projection) : [];
    // Rosters and globals only move on structural edits, and this pass never stringifies.
  }, [wf, snapshot]);

  const nltGlobals = useMemo(() => extractWorkflowGlobals(snapshot), [snapshot]);
  const globalCount = Object.keys(nltGlobals).length;
  const globalBytes = useMemo(() => approxJsonBytes(nltGlobals), [nltGlobals]);
  const warningsKey = nltWarnings.join('|');
  const nltStateCount = wf ? Object.keys(wf.states).length : 0;
  const hasGraph = wf != null;

  const nltBridge = useMemo<WorkflowEditBridge | null>(() => {
    if (!hasGraph) return null;
    // Sizing serializes the states half once per structural change — not per keystroke,
    // since nothing below re-runs while the user is only editing field contents.
    const stateBytes = approxJsonBytes(buildProjectionFromEditable(wfRef.current, null)?.states ?? {});
    return {
      baseRevision: baseDigest,
      stateCount: nltStateCount,
      globalCount,
      stateBytes,
      globalBytes,
      warnings: warningsKey ? warningsKey.split('|') : [],
      stateIds: Object.keys(wfRef.current?.states ?? {}),
      getStates: () => wfRef.current?.states ?? null,
      getProjection: (options) => {
        const projection = buildProjectionFromEditable(wfRef.current, snapshotRef.current);
        if (!projection || options?.includeGlobals !== false) return projection;
        return { ...projection, globals: trimGlobalsToWritableUse(projection.globals) };
      },
      applyProposedStates,
      focusWorkflow: () => setPage('graph'),
    };
    // Keyed on the joined warnings rather than the array, so a fresh array with the same
    // contents does not republish the bridge and re-render the sidebar on every keystroke.
  }, [hasGraph, baseDigest, nltStateCount, globalCount, globalBytes, warningsKey, applyProposedStates]);

  useEffect(() => {
    onNltBridgeChange?.(nltBridge);
  }, [nltBridge, onNltBridgeChange]);

  useEffect(() => () => onNltBridgeChange?.(null), [onNltBridgeChange]);

  // ─── Save flow: dry run → plan → apply ────────────────────────────────────

  const openPlan = async () => {
    if (!wf || !baseDigest) return;
    const problems = validateEditable(wf);
    if (problems.length) {
      setValidationProblems(problems);
      setSideTab('data');
      notifyRef.current('error', `The graph has ${problems.length} problem${problems.length === 1 ? '' : 's'} — see the Data tab.`);
      return;
    }
    setPlan({ ...CLOSED_PLAN, open: true, loading: true });
    try {
      const res = await connector.saveWorkflow(integrationId, {
        base_revision: baseDigest,
        dry_run: true,
        changes: { workflow: editableToDsl(wf) },
      });
      if (isWorkflowApplyResult(res)) {
        // Defensive: the server should never apply on a dry run.
        setPlan(CLOSED_PLAN);
        notifyRef.current('info', `Applied unexpectedly on dry run — revision v${res.revision?.workflow_version ?? '?'}. Reloading.`);
        setDirty(false);
        dirtyRef.current = false;
        void load({ conditional: false, silent: true });
        return;
      }
      setPlan((p) => ({ ...p, loading: false, data: res }));
    } catch (err) {
      const info = describeWorkflowError(err, 'Dry run failed.');
      if (info.status === 409) {
        // Stale base revision: the conflict banner owns this case (same as on apply).
        setPlan(CLOSED_PLAN);
        setConflict(info);
      } else {
        setPlan((p) => ({ ...p, loading: false, error: info }));
      }
    }
  };

  const applyPlan = async () => {
    if (!wf || !baseDigest) return;
    setPlan((p) => ({ ...p, applying: true, error: null }));
    try {
      const res = await connector.saveWorkflow(integrationId, {
        base_revision: baseDigest,
        dry_run: false,
        comment: plan.comment.trim() || 'Edited in AI Tuner',
        changes: { workflow: editableToDsl(wf) },
      });
      setPlan(CLOSED_PLAN);
      const version = isWorkflowApplyResult(res) ? res.revision?.workflow_version : undefined;
      notifyRef.current('success', `Workflow applied${version != null ? ` — now v${version}` : ''}.`);
      setDirty(false);
      dirtyRef.current = false;
      await load({ conditional: false, silent: true });
    } catch (err) {
      const info = describeWorkflowError(err, 'Apply failed.');
      if (info.status === 409) {
        setPlan(CLOSED_PLAN);
        setConflict(info);
      } else {
        setPlan((p) => ({ ...p, applying: false, error: info }));
      }
    }
  };

  // ─── Other actions ────────────────────────────────────────────────────────

  const discardLocalEdits = useCallback(() => {
    if (snapshotRef.current) applySnapshot(snapshotRef.current, etagRef.current);
  }, [applySnapshot]);

  const loadServerRevision = useCallback(() => {
    setDirty(false);
    dirtyRef.current = false;
    if (pending) {
      applySnapshot(pending.snapshot, pending.etag);
      return;
    }
    void load({ conditional: false, silent: true });
  }, [pending, applySnapshot, load]);

  const exportDsl = useCallback(() => {
    if (!wf) return;
    const version = snapshot?.revision?.workflow_version;
    downloadJson(`workflow-${wf.name || 'graph'}${version != null ? `-v${version}` : ''}${dirty ? '-edited' : ''}.json`, editableToDsl(wf));
  }, [wf, snapshot, dirty]);

  const importDsl = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(String(reader.result || ''));
          const next = anyGraphToEditable(parsed);
          setWf(next);
          setDirty(true);
          setValidationProblems([]);
          setSelection(null);
          setLayoutKey((k) => k + 1);
          notifyRef.current('success', `Imported ${Object.keys(next.states).length} states and ${next.edges.length} transitions from ${file.name}.`);
        } catch (err) {
          notifyRef.current('error', err instanceof Error ? err.message : 'Failed to import the file.');
        }
      };
      reader.onerror = () => notifyRef.current('error', 'Failed to read the file.');
      reader.readAsText(file);
    },
    []
  );

  const loadLibrary = useCallback(
    async (archived: boolean) => {
      setLibraryLoading(true);
      try {
        const res = await connector.getWorkflow(integrationId, { include: ['components'], includeArchived: archived });
        if (res.status === 200) {
          setSnapshot((prev) =>
            prev ? { ...prev, components: res.snapshot.components ?? [], malformed: res.snapshot.malformed ?? prev.malformed } : prev
          );
          setIncludeArchived(archived);
        }
      } catch (err) {
        notifyRef.current('error', describeWorkflowError(err, 'Failed to load the component library.').message);
      } finally {
        setLibraryLoading(false);
      }
    },
    [integrationId]
  );

  const onRollbackApplied = useCallback(
    (res: WorkflowRollbackResult) => {
      setRollback({ open: false, target: null });
      notifyRef.current('success', `Rolled back to v${res.to_workflow_version}.`);
      setDirty(false);
      dirtyRef.current = false;
      void load({ conditional: false, silent: true });
    },
    [load]
  );

  // ─── Derived ──────────────────────────────────────────────────────────────

  const components = snapshot?.components || [];
  const malformed = snapshot?.malformed || [];
  const triggerCount = components.filter((c) => c.kind === 'trigger').length;
  const strategyCount = components.filter((c) => c.kind === 'strategy').length;
  const versions = snapshot?.workflow_versions || [];
  const currentVersionId = snapshot?.workflow?.version_id || snapshot?.revision?.workflow_version_id || null;
  const dsl = useMemo(() => (wf ? editableToDsl(wf) : null), [wf]);
  const rev = snapshot?.revision;
  const stateCount = wf ? Object.keys(wf.states).length : 0;

  const selectedEdgeExists = selection?.kind === 'edge' && !!wf?.edges.some((e) => e.id === selection.id);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="cwf">
      <header className="cwf-toolbar">
        <div className="cwf-toolbar__identity">
          <span className="cwf-toolbar__icon"><i className="fas fa-project-diagram" aria-hidden="true" /></span>
          <div className="cwf-toolbar__headings">
            {/* Заголовком стоит имя самого воркфлоу, а не слово «Workflow»: оно
                уже сказано вкладкой и шапкой раздела, и повторять его третий раз
                значит занять строку ничем. Имя же раньше пряталось чипом среди
                приглушённых фактов. */}
            <h2 title={snapshot?.source?.workflow_name ? 'source.workflow_name' : undefined}>
              {snapshot?.source?.workflow_name || 'Workflow'}
            </h2>
            <div className="cwf-toolbar__info">
          {rev ? (
            <>
              <Pill tone="accent" title={rev.workflow_version_id || ''}>
                v{rev.workflow_version ?? '—'}
              </Pill>
              {rev.workflow_status ? <Pill tone={rev.workflow_status === 'published' ? 'ok' : 'neutral'}>{rev.workflow_status}</Pill> : null}
              <span className="cwf-toolbar__sep" aria-hidden="true" />
              <span className="cwf-toolbar__digest" title={`revision.digest ${rev.digest}\nETag ${etag || '—'}`}>
                <i className="fas fa-fingerprint" aria-hidden="true" /> {shortDigest(rev.digest)}
              </span>
              <span className="cwf-muted" title={`taken_at ${rev.taken_at || '—'}`}>
                {formatTimestamp(rev.taken_at)}
              </span>
              <span className="cwf-toolbar__sep" aria-hidden="true" />
              <span className="cwf-muted cwf-toolbar__size">
                {stateCount} states · {wf?.edges.length ?? 0} transitions
              </span>
            </>
          ) : loading ? (
            <span className="cwf-muted">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading workflow…
            </span>
          ) : (
            <span className="cwf-muted">No snapshot loaded.</span>
          )}
          {dirty ? (
            <Pill tone="warn" title="Local edits not yet applied">
              unsaved changes
            </Pill>
          ) : null}
            </div>
          </div>
        </div>
        <div className="cwf-toolbar__actions">
          <button
            type="button"
            className="cwf-btn cwf-btn--sm"
            onClick={() => void load({ conditional: true })}
            disabled={loading}
            title={`Conditional GET with If-None-Match${lastCheckedAt ? ` · last checked ${lastCheckedAt.toLocaleTimeString()}` : ''}`}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
          </button>
          <button type="button" className="cwf-btn cwf-btn--sm" onClick={() => setLayoutKey((k) => k + 1)} disabled={!wf} title="Re-run the automatic layout">
            <i className="fas fa-magic" aria-hidden="true" /> Layout
          </button>
          <button type="button" className="cwf-btn cwf-btn--sm" onClick={handleAddState} disabled={!wf}>
            <i className="fas fa-plus" aria-hidden="true" /> State
          </button>
          {dirty ? (
            <button type="button" className="cwf-btn cwf-btn--sm cwf-btn--ghost" onClick={() => setConfirmDiscard(true)} title="Drop local edits and return to the loaded revision">
              <i className="fas fa-undo" aria-hidden="true" /> Discard
            </button>
          ) : null}
          {isAdmin ? (
            <>
              <button
                type="button"
                className="cwf-btn cwf-btn--sm"
                onClick={() => setRollback({ open: true, target: null })}
                disabled={!snapshot || versions.length === 0}
                title="Roll back to a previous version"
              >
                <i className="fas fa-history" aria-hidden="true" /> Rollback…
              </button>
              <button
                type="button"
                className="cwf-btn cwf-btn--sm cwf-btn--primary"
                onClick={() => void openPlan()}
                disabled={!wf || !dirty || !baseDigest || plan.open}
                title={!dirty ? 'No local changes to save' : 'Dry run first, then apply'}
              >
                <i className="fas fa-save" aria-hidden="true" /> Save…
              </button>
            </>
          ) : null}
        </div>
      </header>

      <nav className="cwf-subnav" aria-label="Workflow pages">
        {PAGE_TABS.map((tab) => {
          const count =
            tab.id === 'library' ? components.length : tab.id === 'triggers' ? triggerCount : tab.id === 'strategies' ? strategyCount : 0;
          return (
            <button
              key={tab.id}
              type="button"
              className={`cwf-subnav__tab ${page === tab.id ? 'cwf-subnav__tab--active' : ''}`}
              onClick={() => setPage(tab.id)}
            >
              <i className={`fas ${tab.icon}`} aria-hidden="true" /> {tab.label}
              {count ? <span className="cwf-subnav__count">{count}</span> : null}
              {tab.id === 'library' && malformed.length ? (
                <span className="cwf-subnav__count cwf-subnav__count--warn" title="malformed cards">
                  {malformed.length}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {!isAdmin && snapshot ? (
        <div className="cwf-readonly-hint">
          <i className="fas fa-lock" aria-hidden="true" /> Read-only: saving and rollback require the admin role. You can still edit locally and download the DSL.
        </div>
      ) : null}

      {loadError ? (
        <Banner
          tone="error"
          title={
            loadError.status === 403
              ? 'No access to this integration (403)'
              : loadError.status === 401
                ? 'Not authenticated (401)'
                : `Failed to load the workflow${loadError.status ? ` (${loadError.status})` : ''}`
          }
          details={loadError.details}
          onDismiss={() => setLoadError(null)}
          actions={
            <button type="button" className="cwf-btn cwf-btn--xs" onClick={() => void load({ conditional: false })}>
              <i className="fas fa-redo" aria-hidden="true" /> Retry
            </button>
          }
        >
          <p>{loadError.message}</p>
        </Banner>
      ) : null}

      {conflict ? (
        <Banner
          tone="warn"
          title="The configuration changed while you were editing (409)"
          details={conflict.details}
          onDismiss={() => setConflict(null)}
          actions={
            <>
              <button type="button" className="cwf-btn cwf-btn--xs" onClick={exportDsl}>
                <i className="fas fa-download" aria-hidden="true" /> Download my edits
              </button>
              <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--primary" onClick={loadServerRevision}>
                <i className="fas fa-cloud-download-alt" aria-hidden="true" /> Load server revision (discards edits)
              </button>
            </>
          }
        >
          <p>
            {conflict.message}
            {conflict.currentRevision ? (
              <>
                {' '}
                Server revision: <code title={conflict.currentRevision}>{shortDigest(conflict.currentRevision, 16)}</code>, yours was based on{' '}
                <code title={baseDigest || ''}>{shortDigest(baseDigest, 16)}</code>.
              </>
            ) : null}{' '}
            Your edits are not overwritten silently — download them, load the server revision, and re-apply what still makes sense.
          </p>
        </Banner>
      ) : null}

      {pending ? (
        <Banner
          tone="info"
          title={`A newer revision is available on the server (v${pending.snapshot.revision?.workflow_version ?? '?'})`}
          onDismiss={() => setPending(null)}
          actions={
            <>
              <button type="button" className="cwf-btn cwf-btn--xs" onClick={exportDsl}>
                <i className="fas fa-download" aria-hidden="true" /> Download my edits
              </button>
              <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--primary" onClick={loadServerRevision}>
                <i className="fas fa-cloud-download-alt" aria-hidden="true" /> Load it (discards edits)
              </button>
              <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--ghost" onClick={() => setPending(null)}>
                Keep editing
              </button>
            </>
          }
        >
          <p>
            Digest <code>{shortDigest(pending.snapshot.revision?.digest, 16)}</code>. Saving on top of your current base will be rejected with 409.
          </p>
        </Banner>
      ) : null}

      {validationProblems.length ? (
        <Banner tone="error" title="The graph cannot be saved yet" details={validationProblems} onDismiss={() => setValidationProblems([])} />
      ) : null}

      {page === 'tuning' ? (
        <WorkflowTuningPage
          integrationId={integrationId}
          bridge={nltBridge}
          datasets={datasets}
          datasetsLoading={datasetsLoading}
          onNotify={(type, message) => notifyRef.current(type, message)}
          onOpenFineTuning={onOpenFineTuning}
        />
      ) : page === 'ontology' ? (
        <WorkflowOntologyPage ontology={snapshot?.ontology} ontologyRevision={rev?.ontology_revision} />
      ) : page !== 'graph' ? (
        <WorkflowComponentsPage
          key={page}
          fixedKind={page === 'triggers' ? 'trigger' : page === 'strategies' ? 'strategy' : undefined}
          components={components}
          malformed={malformed}
          wf={wf}
          includeArchived={includeArchived}
          loading={libraryLoading}
          onToggleArchived={(next) => void loadLibrary(next)}
          onRefresh={() => void loadLibrary(includeArchived)}
          onSelectEdge={selectEdge}
          onSelectState={selectState}
        />
      ) : fullStateViewId && wf?.states[fullStateViewId] ? (
        <WorkflowStateConfiguratorView
          wf={wf}
          stateId={fullStateViewId}
          components={components}
          onPatch={(patch) => patchState(fullStateViewId, patch)}
          onRename={(newId) => handleRenameState(fullStateViewId, newId)}
          onDelete={() => handleDeleteState(fullStateViewId)}
          onSetEntry={() => patchGraph({ entry: fullStateViewId })}
          onSelectEdge={selectEdge}
          onSelectState={selectState}
          onBack={() => setFullStateViewId(null)}
        />
      ) : (
      <div className="cwf-body">
        <div className="cwf-canvas">
          {wf ? (
            <WorkflowCanvas
              wf={wf}
              layoutKey={layoutKey}
              selection={selection}
              onSelect={handleCanvasSelect}
              onConnect={handleConnect}
            />
          ) : loading ? (
            <div className="cwf-canvas__empty">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              <span>Reading the workflow snapshot…</span>
            </div>
          ) : snapshot ? (
            <div className="cwf-canvas__empty">
              <i className="fas fa-sitemap" aria-hidden="true" />
              <span>The snapshot has no <code>workflow</code> section.</span>
            </div>
          ) : (
            <div className="cwf-canvas__empty">
              <i className="fas fa-sitemap" aria-hidden="true" />
              <span>Nothing loaded yet.</span>
            </div>
          )}
          {wf ? (
            <div className="cwf-legend" aria-hidden="true">
              <span>
                <i style={{ background: '#0099FA' }} /> trigger
              </span>
              <span>
                <i style={{ background: '#FF8300' }} /> card
              </span>
              <span>
                <i style={{ background: '#B85400' }} /> flags
              </span>
              <span>
                <i style={{ background: '#0099FA' }} /> expression
              </span>
              <span>
                <i style={{ background: '#7F9AB3' }} /> always / manual
              </span>
            </div>
          ) : null}
        </div>

        <aside className="cwf-side">
          <nav className="cwf-side__tabs" aria-label="Workflow panels">
            <button type="button" className={`cwf-side__tab ${sideTab === 'inspector' ? 'cwf-side__tab--active' : ''}`} onClick={() => setSideTab('inspector')}>
              <i className="fas fa-sliders-h" aria-hidden="true" /> Inspector
            </button>
            <button type="button" className={`cwf-side__tab ${sideTab === 'versions' ? 'cwf-side__tab--active' : ''}`} onClick={() => setSideTab('versions')}>
              <i className="fas fa-history" aria-hidden="true" /> Versions
              {versions.length ? <span className="cwf-side__count">{versions.length}</span> : null}
            </button>
            <button type="button" className={`cwf-side__tab ${sideTab === 'data' ? 'cwf-side__tab--active' : ''}`} onClick={() => setSideTab('data')}>
              <i className="fas fa-box-archive" aria-hidden="true" /> Data
              {validationProblems.length ? <span className="cwf-side__count cwf-side__count--warn">{validationProblems.length}</span> : null}
            </button>
          </nav>

          <div className="cwf-side__body" data-testid="cwf-side-body">
            {sideTab === 'inspector' ? (
              !wf ? (
                <p className="cwf-note">Load a workflow to inspect it.</p>
              ) : selection?.kind === 'edge' && selectedEdgeExists ? (
                <WorkflowEdgeInspector
                  wf={wf}
                  edgeId={selection.id}
                  components={components}
                  onPatch={(patch) => patchEdge(selection.id, patch)}
                  onDelete={() => handleDeleteEdge(selection.id)}
                  onSelectState={selectState}
                />
              ) : (
                <WorkflowGraphInspector
                  wf={wf}
                  snapshot={snapshot}
                  onPatchGraph={patchGraph}
                  onSetInterrupts={setInterrupts}
                  onSelectState={selectState}
                  onAddState={handleAddState}
                />
              )
            ) : null}

            {sideTab === 'versions' ? (
              <WorkflowVersionsPanel
                versions={versions}
                currentVersionId={currentVersionId}
                isAdmin={isAdmin}
                onRollback={(versionId) => setRollback({ open: true, target: versionId })}
              />
            ) : null}

            {sideTab === 'data' ? (
              <WorkflowDataPanel
                dsl={dsl}
                baseDigest={baseDigest}
                problems={validationProblems}
                onExport={exportDsl}
                onImportClick={() => importInputRef.current?.click()}
              />
            ) : null}
          </div>
        </aside>
      </div>
      )}

      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          importDsl(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      <WorkflowPlanModal
        open={plan.open}
        loading={plan.loading}
        applying={plan.applying}
        plan={plan.data}
        error={plan.error}
        baseDigest={baseDigest}
        comment={plan.comment}
        onCommentChange={(next) => setPlan((p) => ({ ...p, comment: next }))}
        onApply={() => void applyPlan()}
        onClose={() => setPlan(CLOSED_PLAN)}
      />

      <WorkflowRollbackModal
        open={rollback.open}
        integrationId={integrationId}
        versions={versions}
        currentVersionId={currentVersionId}
        initialTarget={rollback.target}
        onClose={() => setRollback({ open: false, target: null })}
        onApplied={onRollbackApplied}
      />

      <ConfirmModal
        isOpen={confirmDiscard}
        title="Discard local edits?"
        message="Your unsaved changes to the workflow graph will be dropped and the loaded revision restored."
        confirmText="Discard"
        variant="danger"
        onConfirm={() => {
          setConfirmDiscard(false);
          discardLocalEdits();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />

      <Notification type={notification.type} message={notification.message} isVisible={notification.isVisible} onClose={hideNotification} position="top-right" />
    </div>
  );
};

export default ConnectorWorkflowPanel;
